/**
 * Decision Journal — the data model and every number shown on the page.
 *
 * Kept apart from the view so each figure has one definition:
 *   trade        a decision that took a side (long / short); "wait" is a decision, not a trade
 *   graded       a trade whose outcome is known (target hit, stopped, break-even)
 *   R            the result in units of the risk taken: +planned RR on a target, -1 on a stop,
 *                0 at break-even. The engine's own r_achieved wins when it is present.
 *   win rate     wins / (wins + losses); break-evens are excluded from both sides
 *   expectancy   average R per graded trade
 *   profit factor   R won / R lost (null until there is at least one loss)
 */

export interface JournalEntry {
  decision_id: string;
  at: number;                       // unix seconds
  symbol: string;
  timeframe: string;
  style: string | null;
  action: string;                   // long | short | wait
  direction: string;
  confidence: number | null;        // 0..1
  conviction: number | null;        // 0..1
  because: string | null;
  entry: number | null;
  stop: number | null;
  target: number | null;
  rr: number | null;
  outcome: string | null;
  r_achieved: number | null;
  graded_at?: number | null;        // unix seconds, set by the grading step
  bars_held?: number | null;
  reasoning_trace?: ReasoningTrace | null;   // reasoning's chain of thought (learning-engine journal_entries)
}

// expired   graded: neither level was reached in time; its R is the engine's r_achieved (0 if absent)
// ambiguous the grader could not tell which level was hit first (both inside one bar): not counted
export type Result = 'won' | 'lost' | 'breakeven' | 'expired' | 'ambiguous' | 'open' | 'none';

const WON = new Set(['TARGET_HIT', 'CORRECT']);
const LOST = new Set(['STOPPED', 'WRONG']);
const FLAT = new Set(['BREAK_EVEN', 'NO_MOVE']);
const EXPIRED = new Set(['EXPIRED', 'TIMED_OUT', 'TIMEOUT']);

export const isTrade = (e: JournalEntry): boolean => e.action === 'long' || e.action === 'short';

export function resultOf(e: JournalEntry): Result {
  if (!isTrade(e)) return 'none';
  const o = (e.outcome || '').toUpperCase();
  if (WON.has(o)) return 'won';
  if (LOST.has(o)) return 'lost';
  if (FLAT.has(o)) return 'breakeven';
  if (EXPIRED.has(o)) return 'expired';
  if (o === 'AMBIGUOUS') return 'ambiguous';
  return 'open';                     // null or RUNNING
}

/** Result in R for a graded trade; null while it is open or was never a trade. */
export function rOf(e: JournalEntry): number | null {
  const res = resultOf(e);
  if (res === 'open' || res === 'none' || res === 'ambiguous') return null;
  if (e.r_achieved !== null && e.r_achieved !== undefined && Number.isFinite(e.r_achieved)) return e.r_achieved;
  if (res === 'won') return e.rr !== null && Number.isFinite(e.rr) ? e.rr : 1;
  if (res === 'lost') return -1;
  return 0;                          // break-even, or expired with no r_achieved
}

export interface Stats {
  decisions: number;
  trades: number;
  waits: number;
  open: number;
  graded: number;
  wins: number;
  losses: number;
  breakevens: number;
  expired: number;
  ambiguous: number;
  winRate: number | null;
  netR: number | null;
  expectancy: number | null;
  profitFactor: number | null;
  avgPlannedRR: number | null;
  bestR: number | null;
  worstR: number | null;
}

export function computeStats(rows: JournalEntry[]): Stats {
  const trades = rows.filter(isTrade);
  let wins = 0, losses = 0, breakevens = 0, expired = 0, ambiguous = 0, open = 0, gross = 0, lost = 0;
  const rs: number[] = [];
  for (const t of trades) {
    const res = resultOf(t);
    if (res === 'open') { open += 1; continue; }
    if (res === 'ambiguous') { ambiguous += 1; continue; }
    if (res === 'won') wins += 1;
    else if (res === 'lost') losses += 1;
    else if (res === 'expired') expired += 1;
    else breakevens += 1;
    const r = rOf(t) ?? 0;
    rs.push(r);
    if (r > 0) gross += r; else lost += -r;
  }
  const graded = rs.length;
  const planned = trades.map(t => t.rr).filter((v): v is number => v !== null && Number.isFinite(v));
  return {
    decisions: rows.length,
    trades: trades.length,
    waits: rows.length - trades.length,
    open, graded, wins, losses, breakevens, expired, ambiguous,
    winRate: wins + losses > 0 ? wins / (wins + losses) : null,
    netR: graded ? rs.reduce((a, b) => a + b, 0) : null,
    expectancy: graded ? rs.reduce((a, b) => a + b, 0) / graded : null,
    profitFactor: graded && lost > 0 ? gross / lost : null,
    avgPlannedRR: planned.length ? planned.reduce((a, b) => a + b, 0) / planned.length : null,
    bestR: graded ? Math.max(...rs) : null,
    worstR: graded ? Math.min(...rs) : null,
  };
}

export interface CurvePoint { at: number; r: number; cumulative: number; }

/** Cumulative R over time, oldest graded trade first. */
export function equityCurve(rows: JournalEntry[]): CurvePoint[] {
  let cum = 0;
  return rows
    .filter(e => rOf(e) !== null)
    .sort((a, b) => a.at - b.at)
    .map(e => { const r = rOf(e) as number; cum += r; return { at: e.at, r, cumulative: cum }; });
}

export interface Breakdown { key: string; trades: number; graded: number; wins: number; losses: number; winRate: number | null; netR: number | null; }

export function breakdownBy(rows: JournalEntry[], pick: (e: JournalEntry) => string | null): Breakdown[] {
  const groups = new Map<string, JournalEntry[]>();
  for (const e of rows.filter(isTrade)) {
    const k = pick(e) || 'Unclassified';
    const list = groups.get(k);
    if (list) list.push(e); else groups.set(k, [e]);
  }
  return Array.from(groups.entries())
    .map(([key, list]) => {
      const s = computeStats(list);
      return { key, trades: s.trades, graded: s.graded, wins: s.wins, losses: s.losses, winRate: s.winRate, netR: s.netR };
    })
    .sort((a, b) => b.trades - a.trades);
}

// ── formatting ────────────────────────────────────────────────────────────────────────────────
export const dash = '—';

export function fmtPrice(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return dash;
  const abs = Math.abs(n);
  const digits = abs >= 1000 ? 2 : abs >= 10 ? 3 : 5;
  return n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export function fmtR(n: number | null | undefined, digits = 2): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return dash;
  return `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toFixed(digits)}R`;
}

export function fmtPct(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return dash;
  return `${(n * 100).toFixed(digits)}%`;
}

export function fmtDateTime(at: number): string {
  const d = new Date(at * 1000);
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false });
}

export function fmtAgo(at: number, now = Date.now()): string {
  const s = Math.max(0, Math.floor(now / 1000 - at));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export const titleCase = (s: string): string =>
  s.replace(/_/g, ' ').toLowerCase().replace(/(^|\s)\S/g, c => c.toUpperCase());

export function toCsv(rows: JournalEntry[]): string {
  const head = ['time_utc', 'symbol', 'timeframe', 'style', 'action', 'entry', 'stop', 'target', 'planned_rr',
    'confidence', 'conviction', 'outcome', 'r_achieved', 'result_r', 'bars_held', 'reason', 'decision_id'];
  const cell = (v: unknown): string => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = rows.map(e => [
    new Date(e.at * 1000).toISOString(), e.symbol, e.timeframe, e.style ?? '', e.action, e.entry, e.stop, e.target,
    e.rr, e.confidence, e.conviction, e.outcome ?? '', e.r_achieved, rOf(e), e.bars_held ?? '', e.because ?? '', e.decision_id,
  ].map(cell).join(','));
  return [head.join(','), ...lines].join('\n');
}

// ── reasoning trace (reasoning-engine spine/trace.py, carried by the journal row) ──────────────────
export interface TraceItem {
  side: 'for' | 'against';
  fact: string;
  w: number;
  tf: string | null;
  src: string;
  detail: string;
  counted?: boolean;
}

export interface TraceStep {
  step: string;
  summary: string;
  [key: string]: unknown;
}

export interface ReasoningTrace {
  version?: string;
  trimmed?: boolean;
  steps: TraceStep[];
}

export type TraceTone = 'neutral' | 'pos' | 'neg' | 'warn';

export interface TraceView {
  key: string;
  label: string;
  summary: string;
  tone: TraceTone;
  items: TraceItem[];                                   // evidence, strongest first
  checks: { text: string; found: boolean | null }[];    // confirmations looked for / self-checks
  probs: { name: string; p: number }[];                 // every hypothesis, best first
  watch: string[];                                      // what to watch for next
  futures?: string | null;                              // plan: possible futures (synthetic, never a forecast)
}

export const TRACE_STEP_LABEL: Record<string, string> = {
  observe: 'I see', question: 'I ask what each reading needs', evidence: 'The evidence', test: 'I check the lower timeframe',
  conclude: 'Conclusion', check: 'I argue with myself', self_critique: 'What would prove me wrong', plan: 'Plan',
};

const MAX_TRACE_ITEMS = 8;
const asArray = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? v.filter((x): x is Record<string, unknown> => !!x && typeof x === 'object') : [];
const asBool = (v: unknown): boolean | null => (v === true ? true : v === false ? false : null);

/** The trace as drawable steps; [] when the row carries none (older decisions, reasoning offline). */
export function traceView(trace: ReasoningTrace | null | undefined): TraceView[] {
  if (!trace || !Array.isArray(trace.steps)) return [];
  return trace.steps
    .filter(s => s && typeof s.step === 'string')
    .map((s): TraceView => {
      const v: TraceView = {
        key: s.step, label: TRACE_STEP_LABEL[s.step] ?? titleCase(s.step), summary: String(s.summary ?? ''),
        tone: 'neutral', items: [], checks: [], probs: [], watch: [],
      };
      if (s.step === 'evidence') {
        const winner = asArray(s.hypotheses)[0];
        v.items = asArray(winner?.items)
          .map(i => ({
            side: i.side === 'against' ? 'against' : 'for', fact: String(i.fact ?? ''), w: Number(i.w) || 0,
            tf: i.tf ? String(i.tf) : null, src: String(i.src ?? ''), detail: String(i.detail ?? i.fact ?? ''),
            counted: i.counted === false ? false : undefined,
          }) as TraceItem)
          .sort((a, b) => b.w - a.w)
          .slice(0, MAX_TRACE_ITEMS);
      } else if (s.step === 'test') {
        v.checks = asArray(s.checks).map(c => ({
          text: `${String(c.look_for ?? '')}${c.tf ? ` · ${String(c.tf)}` : ''}`, found: asBool(c.found),
        }));
      } else if (s.step === 'conclude') {
        const p = s.probabilities && typeof s.probabilities === 'object' ? s.probabilities as Record<string, unknown> : {};
        v.probs = Object.entries(p)
          .map(([name, val]) => ({ name, p: Number(val) || 0 }))
          .sort((a, b) => b.p - a.p);
      } else if (s.step === 'check') {
        v.tone = s.conflict ? 'neg' : 'pos';
        v.checks = asArray(s.checks).map(c => ({ text: String(c.detail ?? c.name ?? ''), found: asBool(c.ok) }));
      } else if (s.step === 'self_critique') {
        v.tone = 'warn';
      } else if (s.step === 'plan') {
        const act = s.action === 'act' || v.summary.startsWith('act');
        v.tone = act ? 'pos' : 'warn';
        v.watch = asArray(s.watch).map(w => String(w.for ?? '')).filter(Boolean);
        const pf = s.possible_futures && typeof s.possible_futures === 'object' ? s.possible_futures as Record<string, unknown> : null;
        if (pf && typeof pf.text === 'string' && pf.text) {
          v.futures = pf.text.includes('not a forecast') ? pf.text : `${pf.text}; synthetic futures, not a forecast`;
        }
      }
      return v;
    });
}
